import { useCallback, useRef } from "react";
import type ClientItem from "owa-mail-store/lib/store/schema/ClientItem";

/** A registered reading-pane selection region - a message body or the subject header. */
export interface SelectionRegion {
    item: ClientItem;
    /** true for a message body (full option set), false for the subject header (Copilot only). */
    isBody: boolean;
}

const regionRegistry = new WeakMap<HTMLElement, SelectionRegion>();

/** Registers an element as a selection region. Returns a function that unregisters it. */
export function registerSelectionRegion(element: HTMLElement, region: SelectionRegion): () => void {
    regionRegistry.set(element, region);
    return () => {
        regionRegistry.delete(element);
    };
}

/** Walks up from a node to the nearest registered region, or undefined if none. */
export function resolveSelectionRegion(node: Node | null | undefined): {
    element: HTMLElement;
    region: SelectionRegion;
} | undefined {
    let current: HTMLElement | null = node instanceof HTMLElement ? node : (node?.parentElement ?? null);
    while (current) {
        const region = regionRegistry.get(current);
        if (region) {
            return { element: current, region };
        }
        current = current.parentElement;
    }
    return undefined;
}

/** Callback ref that registers the attached element as a selection region for the item. */
export function useSelectionRegion(item: ClientItem | undefined, isBody: boolean): (element: HTMLElement | null) => void {
    const unregisterRef = useRef<(() => void) | undefined>(undefined);
    return useCallback((element: HTMLElement | null) => {
        unregisterRef.current?.();
        unregisterRef.current = undefined;
        if (element && item) {
            unregisterRef.current = registerSelectionRegion(element, { item, isBody });
        }
    }, [item, isBody]);
}
